<template>
  <div v-loading="saveInProgress" class="showroom-invite-mail-editor">
    <div v-if="mode === EDITOR">
      <el-form ref="mailForm" label-width="120px" :rules="rules" :model="form">
        <el-form-item label="Subject" prop="subject">
          <el-input v-model="form.subject" />
        </el-form-item>
        <el-form-item label="Message body" prop="body">
          <el-input v-model="form.body" type="textarea" autosize />
          <p class="hint text">
            This must include ACTIVATION_LINK (otherwise user won't be able to
            access showroom). Other placeholders that can be used are:
            SHOWROOM_NAME and COMPANY_NAME.
          </p>
        </el-form-item>
        <el-form-item label="Reply-To" prop="replyTo">
          <el-input
            v-model="form.replyTo"
            placeholder="Example: joe.doe@vestbee.com"
          />
        </el-form-item>
      </el-form>

      <div>
        <el-button type="primary" @click="onSave()"> Save </el-button>
        <el-button type="text" @click="toggleMode()"> Cancel </el-button>
      </div>
    </div>

    <div v-if="mode === PREVIEW">
      <ShowroomInviteMailPreview
        :subject="form.subject"
        :body="form.body"
        :reply-to="form.replyTo"
      />

      <div>
        <el-button type="primary" size="small" @click="toggleMode()">
          Edit
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch } from '@vue/composition-api'
import ShowroomInviteMailPreview from './ShowroomInviteMailPreview'
import { isEmail } from 'utils'
import { useStore } from '@/store'

export default {
  components: {
    ShowroomInviteMailPreview,
  },
  setup(props, { root }) {
    const store = useStore()
    const mailForm = ref(null)
    const form = reactive(initForm())

    const showroom = computed(() => store.state.eventDetails.event)

    watch(showroom, (showroom) => {
      const mapping = {
        invite_subject_template: 'subject',
        invite_body_template: 'body',
        invite_reply_to: 'replyTo',
      }
      Object.entries(mapping).forEach(([showroomAttr, formAttr]) => {
        if (showroom[showroomAttr]) {
          form[formAttr] = showroom[showroomAttr]
        }
      })
    })

    const rules = {
      subject: [
        { required: true, message: 'Please provide subject', trigger: 'blur' },
        {
          min: 10,
          message: 'Subject must have at least 10 characters',
          trigger: 'blur',
        },
      ],
      body: [
        { required: true, message: 'Please provide body', trigger: 'blur' },
        {
          validator(rule, value, callback) {
            const hasActivationLink =
              value && value.search('ACTIVATION_LINK') >= 0
            if (!hasActivationLink) {
              callback(
                new Error('Message body must contain ACTIVATION_LINK word.'),
              )
            }
            callback()
          },
          trigger: 'blur',
        },
      ],
      replyTo: [
        {
          validator(rule, value, callback) {
            if (value && !isEmail(value)) {
              callback(
                new Error('This must be either empty or proper e-mail address'),
              )
            }
            callback()
          },
          trigger: 'blur',
        },
      ],
    }

    const EDITOR = 'EDITOR'
    const PREVIEW = 'PREVIEW'
    const mode = ref(PREVIEW)
    const toggleMode = () => {
      if (mode.value === EDITOR) {
        mode.value = PREVIEW
      } else {
        mode.value = EDITOR
      }
    }

    const fetchInProgress = computed(
      () => store.state.eventDetails.fetchInProgress,
    )
    const saveInProgress = computed(
      () => store.state.eventDetails.saveInProgress,
    )
    const onSave = async () => {
      const save = () => {
        return store.dispatch('eventDetails/update', {
          invite_subject_template: form.subject,
          invite_body_template: form.body,
          invite_reply_to: form.replyTo,
        })
      }

      try {
        await mailForm.value.validate()
        await save()
        toggleMode()
      } catch (err) {
        root.$message.error('Please correct form errors first')
      }
    }

    return {
      fetchInProgress,
      mailForm,
      form,
      rules,

      EDITOR,
      PREVIEW,
      mode,

      toggleMode,

      saveInProgress,
      onSave,
    }
  },
}

function initForm() {
  return {
    subject: 'Invitation from COMPANY_NAME',
    replyTo: '',
    body: `Hi,

COMPANY_NAME would like to share with you startups participating in SHOWROOM_NAME. Click on the link below to get access:

ACTIVATION_LINK

If you would like to talk to some of the companies, you can do it directly by clicking on the button "Message".

Please contact me in case of any questions.

Best regards,

Konrad Koncerewicz

Head of VC & Startups
https://vestbee.com`,
  }
}
</script>

<style lang="scss" scoped>
@import '~assets/css/colors.scss';
@import '~assets/css/vars.scss';

.showroom-invite-mail-editor {
  color: $grey90;

  .el-form-item__content {
    .hint {
      color: $grey120;
    }
  }

  .el-form-item {
    margin-bottom: 22px;
  }
}
</style>
